import * as React from 'react';

import {EmptyAddCard} from 'shells/card/empty-add-card';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {SectionLabel} from 'shells/typography/section-label';

import type {
    DripSequenceFragment as DripSequence,
    EditionFragment as DripEdition,
    CampaignUpdateInput,
    EditionUpdateInput,
    StartTriggerSetInput,
    DripSequenceGoalSetInput,
} from 'nutshell-graphql-types';

import {NewEditionModal} from '../../editions/new-edition-modal';
import {convertEmailEngagementStatsToGeneralStatObject} from '../../utils/helpers';
import {useCheckAndResetClientConfigsOnMount} from '../../utils/use-check-and-reset-client-configs-on-mount';

import {DripSequencesDetailsForm} from '../drip-sequences-details/drip-sequences-details-form';
import {emailMarketingGoalTypeValues, emailMarketingTriggerTypeValues} from '../sidebar/constants';

import {DripSequenceEditionsListItem} from './drip-sequence-editions-list-item';
import {GoalCardWrapper} from './goal-card-wrapper';
import {NextStepCardWrapper} from './next-step-card-wrapper';
import {StartTriggerCardWrapper} from './start-trigger-card-wrapper';

import styles from './drip-sequence-page.module.css';

type Props = {
    dripSequence: DripSequence | null | undefined;
    editions: DripEdition[];
    isLoading: boolean;
    isDraft: boolean;
    onRemoveEdition: (editionId: string, campaignId: string) => Promise<any>;
    onDripSequenceUpdate: (input: CampaignUpdateInput) => Promise<any>;
    onEditionUpdate: (input: EditionUpdateInput) => Promise<any>;
    onCreateEdition: (name: string, campaignId: string) => Promise<any>;
    onDuplicateEdition: (
        editionId: string,
        campaignId: string,
        name?: string | null | undefined
    ) => Promise<any>;
    refetchDripSequence: () => Promise<any>;
    refetchDripEditions: () => Promise<any>;
    onSetStartTrigger: (input: StartTriggerSetInput) => Promise<any>;
    onSetGoal: (input: DripSequenceGoalSetInput) => Promise<any>;
};

export function DripSequenceMessage(props: Props) {
    const {
        dripSequence,
        editions,
        isLoading,
        isDraft,
        onRemoveEdition,
        onDripSequenceUpdate,
        onEditionUpdate,
        onCreateEdition,
        onDuplicateEdition,
        refetchDripSequence,
        refetchDripEditions,
        onSetStartTrigger,
        onSetGoal,
    } = props;
    const [isEditingStartTrigger, setIsEditingStartTrigger] = React.useState<
        string | null | undefined
    >(null);
    const [isAddingMessage, setIsAddingMessage] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);

    const {toastState: saveToastState} = useManageSaveState(isSaving, 1000);

    useCheckAndResetClientConfigsOnMount([
        {
            name: 'isEditingStartTrigger',
            action: () => {
                setIsEditingStartTrigger(NutClientConfig.isEditingStartTrigger);
            },
            timeout: 1000,
        },
    ]);

    return (
        <>
            <div className='flex full-height'>
                <div className={styles['left-container']}>
                    <StartTriggerCardWrapper
                        isLoading={isLoading}
                        dripSequence={dripSequence}
                        editions={editions}
                        onSetStartTrigger={onSetStartTrigger}
                        onEditionUpdate={(editionId, sendDelay) => {
                            return onEditionUpdate({editionId, sendDelay});
                        }}
                        isEditingStartTrigger={isEditingStartTrigger}
                        setIsEditingStartTrigger={(value) => setIsEditingStartTrigger(value)}
                        isTriggerEditable={Boolean(
                            dripSequence &&
                                dripSequence.status !== 'CANCELLED' &&
                                dripSequence.status !== 'RETIRED'
                        )}
                        initialDelay={
                            // @ts-expect-error
                            editions.length > 0 && editions[0].delay
                                ? {
                                      // @ts-expect-error
                                      unit: editions[0].delay.unit,
                                      // @ts-expect-error
                                      timespan: editions[0].delay.timespan,
                                  }
                                : null
                        }
                        variant={
                            dripSequence &&
                            dripSequence.status !== 'ACTIVE' &&
                            dripSequence.status !== 'GENERATING' &&
                            dripSequence.status !== 'GENERATED_WITH_ISSUES'
                                ? dripSequence.status
                                : undefined
                        }
                        triggerTypeValues={emailMarketingTriggerTypeValues}
                    />
                    <div className={styles['drip-edition-list-container']}>
                        {isLoading ? (
                            <div className={styles['loading-page-container']}>
                                <LoadingPage padding={0} />
                            </div>
                        ) : (
                            <>
                                {editions.length ? (
                                    <StackLegacy spacing={16}>
                                        {editions.map((edition) => (
                                            <div key={edition.id} id={edition.id}>
                                                <DripSequenceEditionsListItem
                                                    edition={edition}
                                                    // @ts-expect-error
                                                    firstEditionId={editions[0].id}
                                                    onEditionUpdate={(editionId, sendDelay) => {
                                                        return onEditionUpdate({
                                                            editionId,
                                                            sendDelay,
                                                        });
                                                    }}
                                                    refetchDripEditions={refetchDripEditions}
                                                    onRemoveEdition={(editionId: string) => {
                                                        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                                        dripSequence
                                                            ? onRemoveEdition(
                                                                  editionId,
                                                                  dripSequence.id
                                                              )
                                                            : undefined;
                                                    }}
                                                    engagementStats={convertEmailEngagementStatsToGeneralStatObject(
                                                        edition.engagementStats
                                                    )}
                                                />
                                            </div>
                                        ))}
                                    </StackLegacy>
                                ) : undefined}
                                {isDraft ? (
                                    <div className={styles['add-message-container']}>
                                        <EmptyAddCard
                                            onClick={() => setIsAddingMessage(true)}
                                            text='Add a message'
                                            background='off-white'
                                            size='small'
                                        />
                                    </div>
                                ) : undefined}
                            </>
                        )}
                    </div>
                </div>
                <div className={styles['right-container']}>
                    <GoalCardWrapper
                        isLoading={isLoading}
                        dripSequence={dripSequence}
                        // @ts-expect-error
                        editions={editions}
                        onSetGoal={onSetGoal}
                        onDripSequenceUpdate={(input) => {
                            return onDripSequenceUpdate({
                                id: input.id,
                                isStopOnGoal: input.isStopOnGoal,
                            });
                        }}
                        variant={
                            dripSequence &&
                            dripSequence.status !== 'ACTIVE' &&
                            dripSequence.status !== 'GENERATING' &&
                            dripSequence.status !== 'GENERATED_WITH_ISSUES'
                                ? dripSequence.status
                                : undefined
                        }
                        goalTypeValues={emailMarketingGoalTypeValues}
                    />
                    {dripSequence && dripSequence.goal ? (
                        <NextStepCardWrapper
                            isLoading={isLoading}
                            dripSequenceId={dripSequence.id}
                            goal={dripSequence.goal}
                            onSetGoal={onSetGoal}
                            variant={
                                dripSequence.status !== 'ACTIVE' &&
                                dripSequence.status !== 'GENERATING' &&
                                dripSequence.status !== 'GENERATED_WITH_ISSUES'
                                    ? dripSequence.status
                                    : undefined
                            }
                        />
                    ) : undefined}
                    <SectionLabel>
                        <SavedSettingToast state={saveToastState}>
                            Drip sequence details
                        </SavedSettingToast>
                    </SectionLabel>
                    {dripSequence && !isLoading ? (
                        <DripSequencesDetailsForm
                            dripSequence={dripSequence}
                            onDripSave={(saveData) => {
                                setIsSaving(true);

                                return onDripSequenceUpdate(saveData).then(() => {
                                    refetchDripSequence().then(() => {
                                        setIsSaving(false);
                                    });
                                });
                            }}
                            onEditionUpdate={onEditionUpdate}
                            editions={editions}
                        />
                    ) : (
                        <div className={styles['loading-details-container']} />
                    )}
                </div>
            </div>
            {dripSequence ? (
                <NewEditionModal
                    type='dripSequenceEditions'
                    isOpen={isAddingMessage}
                    onClose={() => {
                        setIsAddingMessage(false);
                    }}
                    campaignId={dripSequence.id}
                    dripSequenceId={dripSequence.id}
                    onCreateEdition={onCreateEdition}
                    onDuplicateEdition={onDuplicateEdition}
                    // @ts-expect-error
                    defaultView='dripMessage'
                />
            ) : null}
        </>
    );
}
